import { Component } from '@theme/component';

/**
 * Product Details Wheel — circular controls swap center imagery and content panels.
 */
class ProductDetailsWheel extends Component {
  requiredRefs = ['wheel', 'wheelContainer', 'content', 'centerImages', 'bottomImage'];

  connectedCallback() {
    super.connectedCallback();
    this.items = this.querySelectorAll('.product-details-wheel__item');
    this.buttons = this.querySelectorAll('.product-details-wheel__button');
    this.panels = this.querySelectorAll('.product-details-wheel__panel');
    this._boundClickHandlers = [];
    this._pressClearTimer = null;

    this.buttons.forEach((button, index) => {
      const onClick = () => this.handleItemClick(index);
      this._boundClickHandlers.push({ button, onClick });
      button.addEventListener('click', onClick);
    });

    this._onKeyDown = this.handleKeyDown.bind(this);
    this.addEventListener('keydown', this._onKeyDown);

    const activePanel = this.querySelector('.product-details-wheel__panel--active');
    const initialIndex = activePanel?.dataset.index != null ? parseInt(activePanel.dataset.index, 10) : 0;
    this.currentIndex = Number.isFinite(initialIndex) ? initialIndex : 0;
    this.syncButtonAria(this.currentIndex);

    this._autoAdvanceTimeoutId = null;
    this._autoAdvanceObserver = null;
    this.#setupRevealAutoAdvance();
  }

  /**
   * After the section is on-screen, once, wait 1s then select the next control
   * so users see the wheel change — unless they already picked another tab.
   */
  #setupRevealAutoAdvance() {
    if (this.buttons.length < 2) return;

    let didSchedule = false;
    this._autoAdvanceObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting || didSchedule) return;
        didSchedule = true;
        this._autoAdvanceObserver?.disconnect();
        this._autoAdvanceObserver = null;

        const baselineIndex = this.currentIndex;
        this._autoAdvanceTimeoutId = setTimeout(() => {
          this._autoAdvanceTimeoutId = null;
          if (this.currentIndex !== baselineIndex) return;
          if (!this.#isRoughlyInViewport()) return;
          const next = (baselineIndex + 1) % this.buttons.length;
          this.handleItemClick(next);
        }, 1000);
      },
      { root: null, rootMargin: '0px', threshold: 0.12 }
    );
    this._autoAdvanceObserver.observe(this);
  }

  #isRoughlyInViewport() {
    const r = this.getBoundingClientRect();
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    return r.bottom > 0 && r.top < vh && r.right > 0 && r.left < vw;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._boundClickHandlers.forEach(({ button, onClick }) => {
      button.removeEventListener('click', onClick);
    });
    this._boundClickHandlers = [];
    this.removeEventListener('keydown', this._onKeyDown);
    if (this._pressClearTimer) clearTimeout(this._pressClearTimer);
    if (this._autoAdvanceTimeoutId) {
      clearTimeout(this._autoAdvanceTimeoutId);
      this._autoAdvanceTimeoutId = null;
    }
    this._autoAdvanceObserver?.disconnect();
    this._autoAdvanceObserver = null;
  }

  /**
   * Short press shrink — readable on mobile without relying on hover.
   */
  triggerPressAnimation(button) {
    if (!(button instanceof HTMLButtonElement)) return;
    button.classList.remove('product-details-wheel__button--pressed');
    void button.offsetWidth;
    button.classList.add('product-details-wheel__button--pressed');
    if (this._pressClearTimer) clearTimeout(this._pressClearTimer);
    this._pressClearTimer = setTimeout(() => {
      button.classList.remove('product-details-wheel__button--pressed');
    }, 130);
  }

  syncButtonAria(index) {
    this.buttons.forEach((button, i) => {
      const on = i === index;
      button.setAttribute('aria-pressed', on ? 'true' : 'false');
      button.setAttribute('aria-selected', on ? 'true' : 'false');
    });
  }

  handleItemClick(index) {
    const button = this.buttons[index];
    if (!button) return;

    this.triggerPressAnimation(button);
    if (index === this.currentIndex) return;

    this.updateActiveState(index);
  }

  handleKeyDown(event) {
    if (!this.items.length) return;

    let newIndex = this.currentIndex;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        newIndex = (this.currentIndex + 1) % this.items.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        newIndex = (this.currentIndex - 1 + this.items.length) % this.items.length;
        break;
      case 'Home':
        event.preventDefault();
        newIndex = 0;
        break;
      case 'End':
        event.preventDefault();
        newIndex = this.items.length - 1;
        break;
      default:
        return;
    }

    this.handleItemClick(newIndex);
    this.buttons[newIndex]?.focus();
  }

  updateActiveState(index) {
    this.syncButtonAria(index);

    this.panels.forEach((panel, i) => {
      if (i === index) {
        panel.classList.add('product-details-wheel__panel--active');
        panel.scrollIntoView({ block: 'nearest', behavior: 'auto' });
      } else {
        panel.classList.remove('product-details-wheel__panel--active');
      }
    });

    this.updateBottomImage(index);
    this.currentIndex = index;
  }

  updateBottomImage(index) {
    const button = this.buttons[index];
    if (!button) return;

    const bottomImageUrl = button.getAttribute('data-bottom-image-url');
    const bottomImageAlt = button.getAttribute('data-bottom-image-alt') || '';
    const bottomImageRef = this.refs.bottomImage;

    if (!bottomImageRef || !(bottomImageRef instanceof HTMLElement)) return;

    bottomImageRef.innerHTML = '';

    if (bottomImageUrl) {
      const img = document.createElement('img');
      img.className = 'product-details-wheel__bottom-image';
      img.loading = 'lazy';
      img.src = bottomImageUrl;
      img.alt = bottomImageAlt;
      img.style.opacity = '0';
      img.style.transition = 'opacity 240ms ease';

      bottomImageRef.appendChild(img);
      bottomImageRef.style.display = 'flex';

      requestAnimationFrame(() => {
        img.style.opacity = '1';
      });
    } else {
      bottomImageRef.style.display = 'none';
    }
  }
}

if (!customElements.get('product-details-wheel')) {
  customElements.define('product-details-wheel', ProductDetailsWheel);
}
